// Blind Trust Portal - Two views: Participant (sealed) and Trustee (full access)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export const BlindTrustPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trustStatus, setTrustStatus] = useState(null);
  const [divestitures, setDivestitures] = useState([]);
  const [trusteeHoldings, setTrusteeHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('participant'); // 'participant' or 'trustee'
  
  // Divestiture form state
  const [showDivestitureModal, setShowDivestitureModal] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [divestitureShares, setDivestitureShares] = useState('');
  const [divestitureSalePrice, setDivestitureSalePrice] = useState('');
  const [divestitureReason, setDivestitureReason] = useState('');

  useEffect(() => {
    loadTrustData();
  }, []);

  const loadTrustData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get trust status
      const statusRes = await api.get('/compliance/trust/status');
      setTrustStatus(statusRes.data);

      // Determine view mode based on role and access
      if (statusRes.data.full_access) {
        setViewMode('trustee');
        
        // Load trustee-specific data
        if (statusRes.data.trust_id) {
          const holdingsRes = await api.get(`/compliance/trust/trustee/holdings?trust_id=${statusRes.data.trust_id}`);
          setTrusteeHoldings(holdingsRes.data.holdings || []);
        }
      } else if (statusRes.data.participant_sealed) {
        setViewMode('participant');
      }

      // Load divestitures (both views can see)
      if (statusRes.data.trust_active) {
        // TODO: Add endpoint to get divestitures by participant
        // For now, using mock data
        setDivestitures([]);
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load trust data');
      console.error('Trust data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDivestiture = async () => {
    if (!selectedHolding || !divestitureShares || !divestitureSalePrice || !divestitureReason) {
      toast.error('Please fill all divestiture fields');
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        `/compliance/trust/divestiture?trust_id=${trustStatus.trust_id}`,
        {
          symbol: selectedHolding.symbol,
          security_name: selectedHolding.symbol, // TODO: Add actual security name
          shares: parseFloat(divestitureShares),
          sale_price: parseFloat(divestitureSalePrice),
          reason: divestitureReason
        }
      );

      toast.success(`Divestiture recorded: ${response.data.divestiture.certificate_number}`);
      
      setShowDivestitureModal(false);
      setSelectedHolding(null);
      setDivestitureShares('');
      setDivestitureSalePrice('');
      setDivestitureReason('');
      
      // Reload data
      loadTrustData();

    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record divestiture');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading blind trust portal...</p>
      </div>
    );
  }

  if (!trustStatus || !trustStatus.trust_active) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-[#D1D5DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h2 className="text-xl font-bold text-[#24292E] mb-2">No Active Blind Trust</h2>
            <p className="text-[#586069]">
              {trustStatus?.message || 'You do not have an active qualified blind trust.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // PARTICIPANT VIEW (SEALED)
  if (viewMode === 'participant') {
    return (
      <div className="min-h-screen bg-[#F7F8FA]">
        {/* Large Navy Banner */}
        <div className="bg-[#1B2A4A] text-white">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="flex items-start gap-4">
              <svg className="w-16 h-16 text-[#B8860B] flex-shrink-0 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-3">Qualified Blind Trust</h1>
                <p className="text-lg text-[#B8C5D8] leading-relaxed">
                  Your assets are held in a Qualified Blind Trust in accordance with federal ethics law under 
                  5 C.F.R. § 2634.403 and the Ethics in Government Act of 1978. All investment decisions are managed 
                  independently by your appointed trustee.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* Trust Status Card */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <h2 className="text-lg font-semibold text-[#24292E] mb-4">Trust Status</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-[#586069] mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#28A745] rounded-full"></div>
                  <span className="text-lg font-semibold text-[#24292E]">
                    {trustStatus.status === 'active' ? 'Active' : trustStatus.status}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#586069] mb-1">Creation Date</p>
                <p className="text-lg font-semibold text-[#24292E]">
                  {formatDate(trustStatus.created_at)}
                </p>
              </div>

              <div>
                <p className="text-sm text-[#586069] mb-1">Appointed Trustee</p>
                <p className="text-lg font-semibold text-[#24292E]">
                  {trustStatus.trustee_name}
                </p>
              </div>
            </div>
          </div>

          {/* Estimated Value Range (Obfuscated) */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <h2 className="text-lg font-semibold text-[#24292E] mb-4">Estimated Value Range</h2>
            
            <div className="bg-[#F6F8FA] rounded-lg p-8 text-center">
              <p className="text-sm text-[#586069] mb-2">Total Trust Value (Estimated Range)</p>
              <p className="text-4xl font-bold text-[#24292E]">
                {trustStatus.estimated_value_range || 'Calculating...'}
              </p>
              <p className="text-xs text-[#586069] mt-4">
                Exact holdings and values are sealed per federal ethics requirements. 
                Your trustee maintains detailed records.
              </p>
            </div>
          </div>

          {/* Divestiture History */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
            <h2 className="text-lg font-semibold text-[#24292E] mb-4">Divestiture History</h2>
            
            {divestitures.length === 0 ? (
              <div className="text-center py-8 text-[#586069]">
                No divestitures recorded
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                      <th className="text-left py-3 px-4 font-medium text-[#24292E]">Asset</th>
                      <th className="text-center py-3 px-4 font-medium text-[#24292E]">Date Divested</th>
                      <th className="text-center py-3 px-4 font-medium text-[#24292E]">Certificate Number</th>
                      <th className="text-center py-3 px-4 font-medium text-[#24292E]">Status</th>
                      <th className="text-center py-3 px-4 font-medium text-[#24292E]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {divestitures.map((div, idx) => (
                      <tr key={idx} className="border-b border-[#E1E4E8]">
                        <td className="py-3 px-4 font-semibold text-[#24292E]">{div.security_name}</td>
                        <td className="py-3 px-4 text-center text-[#586069]">{formatDate(div.sale_date)}</td>
                        <td className="py-3 px-4 text-center font-mono text-[#0366D6]">{div.certificate_number}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-1 bg-[#E6F7E6] text-[#28A745] text-xs rounded">
                            Complete
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button className="text-[#0366D6] hover:text-[#0256C5] text-sm transition-colors">
                            Download Certificate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sealed Notice */}
          <div className="bg-[#FFF9E6] border-l-4 border-[#B8860B] rounded p-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-[#B8860B] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-semibold text-[#8B6914] mb-2">Blind Trust Seal in Effect</h3>
                <p className="text-sm text-[#8B6914] mb-3">
                  Your holdings details are cryptographically sealed. You cannot view individual investments, 
                  make investment decisions, or access detailed financial information while serving in your appointed capacity.
                </p>
                <p className="text-sm text-[#8B6914]">
                  All investment decisions are made independently by {trustStatus.trustee_name} without your input 
                  to ensure compliance with federal conflict-of-interest regulations.
                </p>
              </div>
            </div>
          </div>

          {/* OGE Contact */}
          <div className="bg-white rounded-lg border border-[#E1E4E8] p-6 text-center">
            <h3 className="font-semibold text-[#24292E] mb-2">Questions about your Blind Trust?</h3>
            <p className="text-sm text-[#586069] mb-4">
              Contact the Office of Government Ethics for guidance on qualified blind trusts and ethics compliance.
            </p>
            <a
              href="https://www.oge.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0366D6] hover:bg-[#0256C5] text-white font-semibold rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visit OGE.gov
            </a>
          </div>
        </div>
      </div>
    );
  }

  // TRUSTEE VIEW (FULL ACCESS)
  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#24292E]">Blind Trust Management</h1>
              <p className="text-sm text-[#586069] mt-1">
                Trustee Portal - Full Holdings Access
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-3 py-1 bg-[#B8860B] text-white text-sm font-semibold rounded">
                Trustee Role
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Trust Info */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <div className="grid grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-[#586069] mb-1">Trust ID</p>
              <p className="text-sm font-mono font-semibold text-[#24292E]">
                {trustStatus.trust_id?.substring(0, 8)}...
              </p>
            </div>
            <div>
              <p className="text-sm text-[#586069] mb-1">Status</p>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#28A745] rounded-full"></div>
                <span className="text-sm font-semibold text-[#24292E]">Active</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-[#586069] mb-1">Total Value</p>
              <p className="text-lg font-bold text-[#24292E]">
                {formatCurrency(trustStatus.total_value || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-[#586069] mb-1">Holdings</p>
              <p className="text-lg font-bold text-[#24292E]">
                {trustStatus.holdings_count || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Holdings Table */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#24292E]">Trust Holdings</h2>
            <button
              onClick={() => setShowDivestitureModal(true)}
              className="px-4 py-2 bg-[#D73A49] hover:bg-[#B31D28] text-white text-sm font-medium rounded transition-colors"
            >
              Record Divestiture
            </button>
          </div>

          {trusteeHoldings.length === 0 ? (
            <div className="text-center py-12 text-[#586069]">
              No holdings in trust
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Symbol</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Shares</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Cost Basis</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Current Price</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Current Value</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Gain/Loss</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trusteeHoldings.map((holding, idx) => (
                    <tr key={idx} className="border-b border-[#E1E4E8] hover:bg-[#F6F8FA]">
                      <td className="py-3 px-4 font-mono font-semibold text-[#0366D6]">
                        {holding.symbol}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {holding.shares.toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {formatCurrency(holding.cost_basis)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {formatCurrency(holding.current_price)}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold">
                        {formatCurrency(holding.current_value)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={holding.gain_loss >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'}>
                          {formatCurrency(holding.gain_loss)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedHolding(holding);
                            setShowDivestitureModal(true);
                          }}
                          className="text-[#D73A49] hover:text-[#B31D28] text-sm transition-colors"
                        >
                          Divest
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Participant Activity Log */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <h2 className="text-lg font-semibold text-[#24292E] mb-4">Participant Activity Log</h2>
          <div className="text-center py-8 text-[#586069] text-sm">
            Activity log shows login timestamps and page views only (no financial data visible to participant)
          </div>
        </div>
      </div>

      {/* Divestiture Modal */}
      {showDivestitureModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowDivestitureModal(false)} />
          
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="border-b border-[#E1E4E8] px-6 py-4">
                <h3 className="text-xl font-bold text-[#24292E]">Record Divestiture</h3>
              </div>

              <div className="px-6 py-6 space-y-4">
                {selectedHolding && (
                  <div className="bg-[#F6F8FA] rounded-lg p-4 mb-4">
                    <p className="text-sm text-[#586069] mb-1">Selected Holding</p>
                    <p className="text-lg font-bold text-[#24292E] font-mono">{selectedHolding.symbol}</p>
                    <p className="text-sm text-[#586069]">
                      Available: {selectedHolding.shares.toFixed(4)} shares
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#24292E] mb-2">
                    Shares to Divest
                  </label>
                  <input
                    type="number"
                    value={divestitureShares}
                    onChange={(e) => setDivestitureShares(e.target.value)}
                    step="0.0001"
                    placeholder="0.0000"
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded focus:outline-none focus:ring-2 focus:ring-[#0366D6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#24292E] mb-2">
                    Sale Price per Share
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-[#586069]">$</span>
                    <input
                      type="number"
                      value={divestitureSalePrice}
                      onChange={(e) => setDivestitureSalePrice(e.target.value)}
                      step="0.01"
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-[#D1D5DB] rounded focus:outline-none focus:ring-2 focus:ring-[#0366D6]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#24292E] mb-2">
                    Reason for Divestiture
                  </label>
                  <textarea
                    value={divestitureReason}
                    onChange={(e) => setDivestitureReason(e.target.value)}
                    rows={3}
                    placeholder="Enter reason (e.g., Required divestiture per OGE determination)"
                    className="w-full px-4 py-2 border border-[#D1D5DB] rounded focus:outline-none focus:ring-2 focus:ring-[#0366D6]"
                  />
                </div>

                {divestitureShares && divestitureSalePrice && (
                  <div className="bg-[#F6F8FA] rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#586069]">Total Proceeds</span>
                      <span className="text-xl font-bold text-[#24292E]">
                        {formatCurrency(parseFloat(divestitureShares) * parseFloat(divestitureSalePrice))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-[#E1E4E8] px-6 py-4 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDivestitureModal(false);
                    setSelectedHolding(null);
                    setDivestitureShares('');
                    setDivestitureSalePrice('');
                    setDivestitureReason('');
                  }}
                  className="px-6 py-2 border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDivestiture}
                  disabled={loading}
                  className="px-6 py-2 bg-[#D73A49] hover:bg-[#B31D28] disabled:bg-[#D1D5DB] text-white font-semibold rounded transition-colors"
                >
                  {loading ? 'Recording...' : 'Record Divestiture & Generate Certificate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
